// contexts/AuthContext.js
import React, { createContext, useState, useEffect, useContext } from 'react';
import authService from '../services/authService';

// Создаем контекст аутентификации
const AuthContext = createContext(null);

/**
 * Провайдер контекста аутентификации
 * @param {Object} props - Свойства компонента
 * @returns {JSX.Element} - Компонент-провайдер
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Проверка аутентификации при загрузке приложения
  useEffect(() => {
    const checkAuth = async () => {
      if (!authService.isAuthenticated()) {
        setLoading(false);
        return;
      }

      try {
        const userData = await authService.getUserProfile();
        setUser(userData);
      } catch (err) {
        console.error('Ошибка при получении профиля пользователя:', err);
        
        // Пытаемся обновить токен
        const refreshToken = localStorage.getItem('refreshToken');
        if (refreshToken) {
          try {
            await authService.refreshToken(refreshToken);
            const userData = await authService.getUserProfile();
            setUser(userData);
          } catch (refreshErr) {
            console.error('Ошибка при обновлении токена:', refreshErr);
            await authService.logout();
          }
        } else {
          await authService.logout();
        }
      } finally {
        setLoading(false);
      }
    };

    checkAuth();
  }, []);

  /**
   * Вход пользователя в систему
   * @param {string} username - Имя пользователя
   * @param {string} password - Пароль
   * @returns {Promise<boolean>} - Результат операции входа
   */
  const login = async (username, password) => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await authService.login(username, password);
      setUser(data.user);
      return true;
    } catch (err) {
      setError(err.response?.data?.message || 'Ошибка входа. Проверьте логин и пароль.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Выход пользователя из системы
   * @returns {Promise<void>}
   */
  const logout = async () => {
    setLoading(true);
    try {
      await authService.logout();
    } catch (err) {
      console.error('Ошибка при выходе из системы:', err);
    } finally {
      setUser(null);
      setLoading(false);
    }
  };

  // Значение, предоставляемое контекстом
  const value = {
    user,
    loading,
    error,
    login,
    logout,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Хук для использования контекста аутентификации
 * @returns {Object} - Значение контекста аутентификации
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth должен использоваться внутри AuthProvider');
  }
  return context;
};

export default AuthContext;